import { useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import { useAuthStore } from '@/store/auth';
import { readCachedBranding, writeCachedBranding, type CachedBranding } from '@/lib/branding-cache';

/**
 * Settings — tenant-level config blocks + per-entity branding.
 *
 * The shape mirrors what /api/settings.py enforces. Defaults come back
 * fully populated even if the row was just initialized.
 */

export type PayrollDefaults = {
  pay_frequency: 'MONTHLY' | 'BI_MONTHLY' | 'WEEKLY';
  working_days_basis: 'CALENDAR' | 'PAYABLE' | 'FIXED_30';
  lop_basis: 'CALENDAR' | 'PAYABLE';
  rounding_mode: 'HALF_UP' | 'HALF_EVEN' | 'DOWN';
  rounding_precision: 0 | 1 | 2;
  joining_proration: 'CALENDAR' | 'WORKING' | 'NONE';
  show_ytd_on_payslip: boolean;
  show_employer_share_on_payslip: boolean;
};

export type WorkflowDefaults = {
  payroll_run_requires_approval: boolean;
  approver_role: string;
  auto_publish_payslips_on_approval: boolean;
  structure_changes_need_approval: boolean;
  it_declaration_window_start: string | null;
  it_declaration_window_end: string | null;
  it_declaration_freeze_date: string | null;
  proof_submission_deadline: string | null;
  fbp_window_start: string | null;
  fbp_window_end: string | null;
  fbp_claim_cutoff_day: number;
};

export type StatutoryDefaults = {
  default_country: string;
  default_state: string | null;
  default_city_tier: 'METRO' | 'NON_METRO';
  default_tax_regime: 'OLD' | 'NEW' | 'AUTO_LOWER';
  use_pf_wage_ceiling: boolean;
  esic_eligibility_check: boolean;
};

export type DisplayFormat = {
  number_format: 'INDIAN' | 'INTERNATIONAL';
  date_format: 'DD-MM-YYYY' | 'YYYY-MM-DD' | 'MM/DD/YYYY';
  first_day_of_week: 'SUN' | 'MON';
  default_page_size: number;
};

export type FnFPolicy = {
  notice_period_days: number;
  notice_recovery_basis: 'BASIC_DA' | 'GROSS' | 'FULL_CTC';
  notice_recovery_capped_at_notice: boolean;
  leave_encash_basis: 'BASIC_DA' | 'GROSS';
  leave_encash_divisor: number;
  leave_encash_max_days_cap: number | null;
  leave_encash_tax_exempt_section: string;
  gratuity_min_years: number;
  gratuity_relax_to_4y_240d: boolean;
  gratuity_divisor: number;
  /** null = statutory cap for the date of separation. */
  gratuity_max_amount: number | null;
  deduct_loans_in_full: boolean;
  deduct_advances_in_full: boolean;
  skip_in_regular_run_after_lwd: boolean;
};

export type SsoBlock = {
  uknowva_sync_role: boolean;
  uknowva_callback_timeout: number;
};

export type SsoSource = 'db' | 'env' | 'unset';

/** The uKnowva this workspace is paired with — the one every ?ntk= sign-in
 *  is checked against. Read-only: it is the same pairing the data sync uses. */
export type UknowvaConnection = {
  instance_url: string | null;
  source: string | null;
  api_token_stored: boolean;
  managed_by: 'platform' | 'workspace';
  problem: string | null;
};

export type SsoSettings = {
  stored: SsoBlock;
  effective: SsoBlock;
  effective_source: Record<keyof SsoBlock, SsoSource>;
  connection?: UknowvaConnection;   // only on the dedicated /settings/sso endpoints
};

export type Settings = {
  id: string;
  payroll_defaults: PayrollDefaults;
  workflow_defaults: WorkflowDefaults;
  statutory_defaults: StatutoryDefaults;
  display_format: DisplayFormat;
  fnf_policy: FnFPolicy;
  sso: SsoSettings;
  updated_at: string | null;
};

export type SettingsPatch = Partial<Pick<Settings,
  'payroll_defaults' | 'workflow_defaults' | 'statutory_defaults' | 'display_format' | 'fnf_policy'>>;

// ── SSO settings (dedicated endpoints) ─────────────────────────────────────

export function useSsoSettings() {
  return useQuery({
    queryKey: ['settings', 'sso'],
    queryFn: () => api.get<SsoSettings>('/settings/sso'),
    staleTime: 15 * 1000,
  });
}

export function useUpdateSsoSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<SsoBlock>) => api.put<SsoSettings>('/settings/sso', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['settings', 'sso'] });
      qc.invalidateQueries({ queryKey: ['settings'] });
    },
  });
}

export type SsoTestResult = {
  ok: boolean;
  instance_url: string;
  source: string;
  hrms_status: boolean | null;
  hrms_message: string | null;
};

export function useTestSsoConnection() {
  return useMutation({
    mutationFn: () => api.post<SsoTestResult>('/settings/sso/test-connection', {}),
  });
}

// ── Employee portal visibility (dedicated endpoints) ───────────────────────
// What the ESS pages show of the data HR already holds. Nothing is synced to
// the employee side; this only decides what My Attendance / My Profile reveal.

export type EssPortalBlock = {
  attendance_enabled: boolean;
  attendance_daily: boolean;
  attendance_times: boolean;
  attendance_scope: 'ALL' | 'LOCKED';
  attendance_months: number;
  profile_sections: Record<string, boolean>;
  profile_show_empty: boolean;
};

export type EssPortalSettings = EssPortalBlock & {
  sections: { key: string; label: string }[];
  attendance_scopes: string[];
};

export function useEssPortalSettings() {
  return useQuery({
    queryKey: ['settings', 'ess-portal'],
    queryFn: () => api.get<EssPortalSettings>('/settings/ess-portal'),
    staleTime: 15 * 1000,
  });
}

export function useUpdateEssPortalSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<EssPortalBlock>) => api.put<EssPortalSettings>('/settings/ess-portal', body),
    onSuccess: (data) => {
      qc.setQueryData(['settings', 'ess-portal'], data);
      // The admin may well be an employee too — refresh their own ESS view.
      qc.invalidateQueries({ queryKey: ['ess'] });
    },
  });
}

/** `enabled`: false for a reader without settings.general.view, who would be refused. */
export function useSettings(enabled = true) {
  return useQuery({
    queryKey: ['settings'],
    queryFn: () => api.get<Settings>('/settings'),
    staleTime: 30 * 1000,
    enabled,
  });
}

export function useUpdateSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SettingsPatch) => api.patch<Settings>('/settings', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['settings'] }),
  });
}

// ── Branding ────────────────────────────────────────────────────────────────

export type EntityBranding = {
  logo_b64: string | null;
  primary_color: string | null;
  primary_button_color: string | null;
  /** Everything below also arrives with the uKnowva theme sync. */
  logo_icon_b64?: string | null;
  sidebar_text_color?: string | null;
  header_color?: string | null;
  text_color?: string | null;
  link_color?: string | null;
  secondary_button_color?: string | null;
  mobile_theme_color?: string | null;
  font_family?: string | null;
  font_size?: number | null;
  display_name: string | null;
  payslip_header_html: string | null;
  payslip_footer_html: string | null;
  /** When the uKnowva theme last branded this entity; read-only, never sent back. */
  uknowva_synced_at?: string | null;
};

export function useEntityBranding(entityId: string | null) {
  // Signed-in only: public pages have a stale entity id but no context (would 400).
  const authed = useAuthStore((s) => s.isAuthenticated);
  return useQuery({
    queryKey: ['entity-branding', entityId],
    queryFn: () => api.get<EntityBranding>(`/settings/entities/${entityId}/branding`),
    enabled: !!entityId && authed,
    staleTime: 60 * 1000,
  });
}

/**
 * Effective branding for the app chrome (sidebar + theme). Returns the live
 * value once loaded, but falls back to a synchronous localStorage snapshot on
 * the very first render so a hard refresh paints the entity's logo/colour
 * immediately instead of flashing the default blue + uKnowva logo. The snapshot
 * is refreshed whenever live data arrives.
 */
/** An entity's company details exactly as documents print them, gathered
 *  from Settings › Branding (name, logo, colour) and TDS › Company Info
 *  (address, PAN, TAN, GSTIN, contact). Readable by anyone signed in. */
export type CompanyProfile = {
  entity_id: string;
  name: string;
  address: string;
  registrations: { label: string; value: string }[];
  email: string | null;
  phone: string | null;
  website: string | null;
  logo_src: string | null;
  brand_color: string;
};

export function useCompanyProfile(entityId: string | null) {
  const authed = useAuthStore((s) => s.isAuthenticated);
  return useQuery({
    queryKey: ['company-profile', entityId],
    queryFn: () => api.get<CompanyProfile>(`/settings/entities/${entityId}/company-profile`),
    enabled: !!entityId && authed,
  });
}

export function useActiveBranding(entityId: string | null): CachedBranding | null {
  const q = useEntityBranding(entityId);
  const cached = useMemo(() => readCachedBranding(entityId), [entityId]);
  useEffect(() => {
    if (q.data) writeCachedBranding(entityId, q.data);
  }, [q.data, entityId]);
  return (q.data as CachedBranding | undefined) ?? cached;
}

export function useUpdateEntityBranding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { entityId: string; body: EntityBranding }) =>
      api.put<EntityBranding>(`/settings/entities/${vars.entityId}/branding`, vars.body),
    onSuccess: (_d, vars) => {
      void qc.invalidateQueries({ queryKey: ['entity-branding', vars.entityId] });
      void qc.invalidateQueries({ queryKey: ['company-profile', vars.entityId] });
    },
  });
}

export function useClearEntityBranding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (entityId: string) => api.delete<EntityBranding>(`/settings/entities/${entityId}/branding`),
    onSuccess: (_d, entityId) => {
      void qc.invalidateQueries({ queryKey: ['entity-branding', entityId] });
      void qc.invalidateQueries({ queryKey: ['company-profile', entityId] });
    },
  });
}

// ── Per-entity FnF policy overrides ────────────────────────────────────────

export type EntityFnFPolicy = {
  entity_id: string;
  overrides: Partial<FnFPolicy>;     // null keys fall through to tenant
  effective: FnFPolicy;               // merged (override > tenant > default)
  defaults: FnFPolicy;
};

export function useEntityFnFPolicy(entityId: string | null) {
  return useQuery({
    queryKey: ['entity-fnf-policy', entityId],
    queryFn: () => api.get<EntityFnFPolicy>(`/settings/entities/${entityId}/fnf-policy`),
    enabled: !!entityId,
  });
}

export function useUpdateEntityFnFPolicy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { entityId: string; overrides: Partial<FnFPolicy> }) =>
      api.put<EntityFnFPolicy>(`/settings/entities/${vars.entityId}/fnf-policy`, vars.overrides),
    onSuccess: (_d, vars) => qc.invalidateQueries({ queryKey: ['entity-fnf-policy', vars.entityId] }),
  });
}

export function useClearEntityFnFPolicy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (entityId: string) =>
      api.delete<EntityFnFPolicy>(`/settings/entities/${entityId}/fnf-policy`),
    onSuccess: (_d, entityId) => qc.invalidateQueries({ queryKey: ['entity-fnf-policy', entityId] }),
  });
}
